import { Mail, Smartphone } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Skeleton } from "@/components/ui/skeleton";
import { Switch } from "@/components/ui/switch";
import type { NotificationPrefCategory, NotificationPrefChannel } from "@/gql/graphql";
import { useNotificationPreferences } from "../hooks/use-notification-preferences";

interface CategoryMeta {
  category: NotificationPrefCategory;
  labelKey: string;
  labelFallback: string;
  descriptionKey: string;
  descriptionFallback: string;
}

// Display order and copy for each configurable category. ACCOUNT/security is intentionally
// excluded — those notifications are always delivered.
const CATEGORY_META: CategoryMeta[] = [
  {
    category: "BOOKINGS",
    labelKey: "profile.notifications.categories.bookings.label",
    labelFallback: "Bookings",
    descriptionKey: "profile.notifications.categories.bookings.description",
    descriptionFallback: "New, cancelled, and assigned appointments",
  },
  {
    category: "REMINDERS",
    labelKey: "profile.notifications.categories.reminders.label",
    labelFallback: "Reminders",
    descriptionKey: "profile.notifications.categories.reminders.description",
    descriptionFallback: "Unconfirmed appointment and shift reminders",
  },
  {
    category: "SCHEDULE",
    labelKey: "profile.notifications.categories.schedule.label",
    labelFallback: "Schedule",
    descriptionKey: "profile.notifications.categories.schedule.description",
    descriptionFallback: "Changes to your work schedule",
  },
  {
    category: "DAILY_SUMMARY",
    labelKey: "profile.notifications.categories.dailySummary.label",
    labelFallback: "Daily summary",
    descriptionKey: "profile.notifications.categories.dailySummary.description",
    descriptionFallback: "End-of-day summary of activity",
  },
];

interface AccountNotificationSettingsProps {
  className?: string;
}

/**
 * Account-level grid letting the user turn push and email on or off per notification category.
 */
export function AccountNotificationSettings({ className }: AccountNotificationSettingsProps) {
  const { t } = useTranslation("settings");
  const { preferences, loading, saving, setPreference } = useNotificationPreferences();

  const prefByCategory = new Map(preferences.map((pref) => [pref.category, pref]));

  const handleToggle = (
    category: NotificationPrefCategory,
    channel: NotificationPrefChannel,
    enabled: boolean
  ) => {
    void setPreference(category, channel, enabled);
  };

  if (loading) {
    return (
      <div className={className}>
        <div className="space-y-3">
          {CATEGORY_META.map((meta) => (
            <Skeleton key={meta.category} className="h-14 w-full" />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className={className}>
      <div className="space-y-1">
        <p className="text-sm font-medium">
          {t("profile.notifications.title", "Choose what you get notified about")}
        </p>
        <p className="text-sm text-muted-foreground">
          {t(
            "profile.notifications.subtitle",
            "These control push and email only. The in-app activity feed always shows everything. Account & security alerts are always sent."
          )}
        </p>
      </div>

      {/* Channel header */}
      <div className="mt-4 grid grid-cols-[1fr_auto] items-center gap-x-8">
        <span />
        <div className="flex items-center gap-6 text-xs font-medium text-muted-foreground">
          <span className="flex w-10 items-center justify-center gap-1">
            <Smartphone className="h-3.5 w-3.5" />
            {t("profile.notifications.channels.push", "Push")}
          </span>
          <span className="flex w-10 items-center justify-center gap-1">
            <Mail className="h-3.5 w-3.5" />
            {t("profile.notifications.channels.email", "Email")}
          </span>
        </div>
      </div>

      <div className="mt-2 divide-y">
        {CATEGORY_META.map((meta) => {
          const pref = prefByCategory.get(meta.category);
          const push = pref?.push ?? true;
          const email = pref?.email ?? true;

          return (
            <div
              key={meta.category}
              className="grid grid-cols-[1fr_auto] items-center gap-x-8 py-3"
            >
              <div className="space-y-0.5">
                <p className="text-sm font-medium">{t(meta.labelKey, meta.labelFallback)}</p>
                <p className="text-sm text-muted-foreground">
                  {t(meta.descriptionKey, meta.descriptionFallback)}
                </p>
              </div>
              <div className="flex items-center gap-6">
                <div className="flex w-10 justify-center">
                  <Switch
                    checked={push}
                    disabled={saving}
                    onCheckedChange={(checked) => handleToggle(meta.category, "PUSH", checked)}
                    aria-label={`${t(meta.labelKey, meta.labelFallback)} ${t(
                      "profile.notifications.channels.push",
                      "Push"
                    )}`}
                  />
                </div>
                <div className="flex w-10 justify-center">
                  <Switch
                    checked={email}
                    disabled={saving}
                    onCheckedChange={(checked) => handleToggle(meta.category, "EMAIL", checked)}
                    aria-label={`${t(meta.labelKey, meta.labelFallback)} ${t(
                      "profile.notifications.channels.email",
                      "Email"
                    )}`}
                  />
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
